/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */

import {reshape} from '../../ops/reshape';
import {getGlobalTensorClass, Scalar, Tensor} from '../../tensor';
import {Rank} from '../../types';
import {assert} from '../../util';

declare module '../../tensor' {
  interface Tensor<R extends Rank = Rank> {
    asScalar<T extends Tensor>(): Scalar;
  }
}

/**
 * Converts a size-1 `tf.Tensor` to a `tf.Scalar`.
 * @doc {heading: 'Tensors', subheading: 'Classes'}
 */
getGlobalTensorClass().prototype.asScalar = function<T extends Tensor>(this: T):
    Scalar {
  this.throwIfDisposed();
  assert(this.size === 1, () => 'The array must have only 1 element.');
  return reshape(this, []);
};
